/**
 * RiskPage — Portfolio Risk Analyzer frontend (Phase A, BB-only).
 *
 * Gate: user.bbProgramEnabled === true (set by admin on BB program members).
 * Phase A: single-account at a time via dropdown picker; manual trigger only.
 * Task 14 adds multi-account analyze-many-at-once + combined view.
 *
 * Educational framing throughout — this is observational analysis, not advice.
 * No Execute button anywhere per v3 spec.
 */
import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import * as riskService from '../../services/riskService';
import RiskAnalysisView from './RiskAnalysisView';
import HistoryPanel from './HistoryPanel';
import '../Dashboard.css';
import './RiskPage.css';

// ─── BB gate ──────────────────────────────────────────────────────────────────

const NotInProgram: React.FC = () => (
  <div className="dashboard-container">
    <Navigation />
    <div className="dashboard-content">
      <div style={{ padding: '2rem', maxWidth: 520 }}>
        <h2>Portfolio Risk Analyzer</h2>
        <p style={{ color: '#9ca3af', marginTop: '0.75rem' }}>
          Portfolio Risk Analyzer is available to Boring Bucket program members only.
          If you are a member and see this message, contact support.
        </p>
      </div>
    </div>
  </div>
);

// ─── Skeleton loader ──────────────────────────────────────────────────────────

const AnalysisSkeletonLoader: React.FC = () => (
  <div className="analysis-skeleton">
    {[1, 2, 3].map((i) => (
      <div key={i} className="skeleton-block" />
    ))}
    <p className="skeleton-hint">Analysis in progress (10-20 seconds)...</p>
  </div>
);

interface AccountOption {
  accountId: string;
  label: string;
}

// ─── Inner content (mounted only when BB gate passes) ─────────────────────────

interface InnerProps {
  user: NonNullable<ReturnType<typeof useAuth>['user']>;
  token: string | null;
}

const RiskPageInner: React.FC<InnerProps> = ({ user, token }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [current, setCurrent] = useState<riskService.AnalysisResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [loadingHistorical, setLoadingHistorical] = useState(false);

  const accountOptions: AccountOption[] = useMemo(() => {
    const out: AccountOption[] = [];
    const seen = new Set<string>();
    const conns = user.tastyTradeConnections || [];
    for (const c of conns) {
      if (!c.accountNumber || seen.has(c.accountNumber)) continue;
      seen.add(c.accountNumber);
      const masked = c.accountNumberMasked || '***' + c.accountNumber.slice(-4);
      const nickname = c.label || c.accountType || 'Account';
      out.push({ accountId: c.accountNumber, label: `${nickname} (${masked})` });
    }
    if (out.length === 0 && user.tastyTradeAccountNumber) {
      const a = user.tastyTradeAccountNumber;
      out.push({ accountId: a, label: `Primary (***${a.slice(-4)})` });
    }
    return out;
  }, [user.tastyTradeConnections, user.tastyTradeAccountNumber]);

  const [accountId, setAccountId] = useState<string | undefined>(
    user.tastyTradeAccountNumber || accountOptions[0]?.accountId
  );

  const handleAnalyze = async () => {
    if (!token) return;
    if (!accountId) {
      setError('No TastyTrade account connected. Connect an account in your profile first.');
      return;
    }
    setAnalyzing(true);
    setError(null);
    setRateLimited(false);
    try {
      const result = await riskService.analyzeRisk(token, [accountId]);
      setCurrent(result);
      setHistoryRefresh((n) => n + 1);
    } catch (e: any) {
      if (e.status === 429) {
        setRateLimited(true);
        const nextAt = e.body?.error?.next_available_at || 'midnight ET';
        setError(`You have used today's manual analysis. Next available at ${nextAt}.`);
      } else if (e.status === 503) {
        setError('Risk analyzer is temporarily disabled.');
      } else if (e.status === 403) {
        setError(e.body?.error?.message || 'Not authorized.');
      } else {
        setError(e.message || 'Analysis failed. Please try again.');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSelectHistory = async (analysisId: string) => {
    if (!token) return;
    setLoadingHistorical(true);
    setError(null);
    try {
      const { analysis } = await riskService.getAnalysis(token, analysisId);
      setCurrent({
        analysisId: analysis.analysisId,
        runAt: analysis.runAt,
        mode: analysis.mode,
        accountIds: analysis.accountIds || [],
        analyses: analysis.analyses || {},
        meta: { wall_clock_ms: analysis.wallClockMs || 0 },
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      setError(e.message || 'Could not load that analysis.');
    } finally {
      setLoadingHistorical(false);
    }
  };

  const primaryAccountId = current?.accountIds?.[0];
  const analysisData = primaryAccountId
    ? current?.analyses?.[primaryAccountId]
    : null;
  const displayedAccountLabel = primaryAccountId
    ? (accountOptions.find(o => o.accountId === primaryAccountId)?.label || `***${primaryAccountId.slice(-4)}`)
    : null;

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content">
        <div className="risk-page">
          {/* Header */}
          <header className="risk-page-header">
            <h1>Portfolio Risk Analysis</h1>
            <div className="risk-disclaimer-banner">
              Educational analysis only &mdash; not investment advice.
              WFS LLC is not a registered investment adviser. All observations
              are for informational purposes only.
            </div>
          </header>

          {/* Account picker + trigger */}
          <section className="risk-controls">
            {accountOptions.length === 0 ? (
              <p className="account-display no-account">
                No TastyTrade account connected.
              </p>
            ) : accountOptions.length === 1 ? (
              <p className="account-display">
                Account: {accountOptions[0].label}
              </p>
            ) : (
              <div className="account-picker">
                <label htmlFor="risk-account-select">Account:</label>
                <select
                  id="risk-account-select"
                  value={accountId || ''}
                  onChange={(e) => {
                    setAccountId(e.target.value);
                    setCurrent(null);
                    setError(null);
                  }}
                  disabled={analyzing}
                >
                  {accountOptions.map((opt) => (
                    <option key={opt.accountId} value={opt.accountId}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              className="risk-analyze-btn"
              onClick={handleAnalyze}
              disabled={analyzing || rateLimited || !accountId}
            >
              {analyzing ? 'Analyzing...' : 'Analyze Risk'}
            </button>

            {error && (
              <div className="risk-error-msg">{error}</div>
            )}

            {(analyzing || loadingHistorical) && <AnalysisSkeletonLoader />}
          </section>

          {/* Current analysis result */}
          {analysisData && current && !loadingHistorical && (
            <>
              {displayedAccountLabel && (
                <div className="displayed-account-banner">
                  Showing analysis for: <strong>{displayedAccountLabel}</strong>
                </div>
              )}
              <RiskAnalysisView
                analysis={analysisData}
                mode={current.mode}
                runAt={current.runAt}
                accountLabel={displayedAccountLabel || 'portfolio'}
              />
            </>
          )}

          {/* History */}
          <HistoryPanel
            onSelect={handleSelectHistory}
            selectedId={current?.analysisId}
            refreshKey={historyRefresh}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Main page (gate only) ────────────────────────────────────────────────────

export const RiskPage: React.FC = () => {
  const { user, token } = useAuth();
  if (!user || user.bbProgramEnabled !== true) {
    return <NotInProgram />;
  }
  return <RiskPageInner user={user} token={token} />;
};

export default RiskPage;
